import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  buildColumnWeeks,
  buildColumnWeeksRelative,
  buildColumnDaysGroupedByWeek,
  ColumnBuilder
} from './utils';

import type {
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
  GridValidRowModel
} from '@mui/x-data-grid-pro';
import type { RosterDay, TimeLineView } from '../../types';

export const useRosterTimelineColumns = <
  R extends GridValidRowModel = GridValidRowModel,
  V extends string = never
>({
  rosterDays,
  timelineView,
  renderTimelineCell,
  customViewBuilders = {} as Partial<Record<V, ColumnBuilder<R>>>,
  timelineColumnWidth,
  timelineRelativeStartDate,
  stretchTimelineColumns
}: {
  rosterDays: RosterDay[];
  timelineView: TimeLineView | V;
  renderTimelineCell?: (params: GridRenderCellParams<R>) => ReactNode;
  customViewBuilders?: Partial<Record<V, ColumnBuilder<R>>>;
  timelineColumnWidth?: number;
  timelineRelativeStartDate?: Date;
  stretchTimelineColumns?: boolean;
}): {
  timeLineColumns: GridColDef<R>[];
  columnGroupingModel: GridColumnGroupingModel;
} => {
  const { t } = useTranslation('common');

  return useMemo(() => {
    const weekLabel = t('week');
    const weekAbbr = t('weekAbbr');
    const builderParams = {
      rosterDays,
      weekLabel,
      renderTimelineCell,
      columnWidth: timelineColumnWidth,
      stretchTimelineColumns
    };

    // Check if it's a custom view first
    const customBuilder = customViewBuilders[timelineView as V];
    if (customBuilder) {
      return customBuilder({
        ...builderParams,
        weekLabel: weekAbbr
      });
    }

    // Built-in views
    switch (timelineView) {
      case 'weeksOnly':
        return buildColumnWeeks({
          ...builderParams,
          weekLabel: weekAbbr
        });
      case 'weeksOnlyRelative':
        return buildColumnWeeksRelative({
          ...builderParams,
          weekLabel: weekAbbr,
          startDate:
            timelineRelativeStartDate ?? rosterDays[0]?.date ?? new Date()
        });
      case 'daysByWeek':
        return buildColumnDaysGroupedByWeek(builderParams);
      // TODO: implement other view modes (months, quarters, relative weeks, etc)
      default:
        return buildColumnDaysGroupedByWeek(builderParams);
    }
  }, [
    rosterDays,
    t,
    timelineView,
    customViewBuilders,
    renderTimelineCell,
    timelineColumnWidth,
    timelineRelativeStartDate,
    stretchTimelineColumns
  ]);
};
